import { parseChallenge, parseExercises } from '@/domain/parseChallenge'
import type { Challenge } from '@/domain/workout'
import type { Exercise } from '@/domain/exercise'
import { RETO_AGOSTO_2026 } from '@/data/challenges/agosto-2026'
import { supabase } from './supabase'

/** El reto que la app trae incluido: lo que se ve mientras no haya nada mejor. */
export const FALLBACK_CHALLENGE = RETO_AGOSTO_2026

export interface PublishedChallenge {
  readonly challenge: Challenge
  readonly exercises: Record<string, Exercise>
  /** De dónde salió: sirve para saber si conviene reintentar. */
  readonly source: 'red' | 'cache' | 'incluido'
}

const CACHE_KEY = 'tfc:reto-publicado'

interface CachedPayload {
  version: 1
  savedAt: string
  raw: unknown
}

/** Guarda la última versión descargada para poder abrir la app sin cobertura. */
function writeCache(raw: unknown): void {
  try {
    const payload: CachedPayload = { version: 1, savedAt: new Date().toISOString(), raw }
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Sin espacio para la caché se sigue tirando de red.
  }
}

function readCache(): unknown {
  try {
    const stored = localStorage.getItem(CACHE_KEY)
    if (!stored) return null
    const payload = JSON.parse(stored) as Partial<CachedPayload>
    return payload.version === 1 ? (payload.raw ?? null) : null
  } catch {
    return null
  }
}

function build(raw: unknown, source: PublishedChallenge['source']): PublishedChallenge | null {
  const challenge = parseChallenge(raw)
  if (!challenge) return null

  const exercises =
    typeof raw === 'object' && raw !== null ? parseExercises((raw as { exercises?: unknown }).exercises) : {}

  return { challenge, exercises, source }
}

/**
 * El reto que hay que mostrar ahora mismo, sin esperar a la red: primero la
 * última copia descargada y, si no hay, el reto incluido en la app.
 */
export function loadCachedChallenge(): PublishedChallenge {
  const cached = readCache()
  const parsed = cached !== null ? build(cached, 'cache') : null
  return parsed ?? { challenge: FALLBACK_CHALLENGE, exercises: {}, source: 'incluido' }
}

/**
 * Pide a la base de datos el reto publicado. Devuelve null si no hay conexión,
 * si no está configurada o si lo que llega no es válido: en todos esos casos
 * el alumno se queda con lo que ya tenía y puede entrenar igual.
 */
export async function fetchPublishedChallenge(challengeId: string): Promise<PublishedChallenge | null> {
  if (!supabase) return null

  try {
    const response = await supabase.rpc('challenge_json', { p_challenge_id: challengeId })
    // Sin tipos generados, la respuesta llega como `any`: se trata como
    // desconocida y es el validador quien decide si sirve.
    const data: unknown = response.data
    if (response.error || data === null) return null

    const result = build(data, 'red')
    if (!result) return null

    writeCache(data)
    return result
  } catch {
    return null
  }
}
